import { FC } from "react"
import { useTranslation } from "react-i18next"

import accountHelp from "../../../assets/images/account-help.jpg"
import gettingStartedHelp from "../../../assets/images/getting-started-help.jpg"
import orderHelp from "../../../assets/images/order-help.jpg"
import printHelp from "../../../assets/images/print-help.png"
import scannerIntegrationHelp from "../../../assets/images/scanner-integration.png"
import patientListHelp from "../../../assets/images/patient-list-help.jpg"
import treatmentHelp from "../../../assets/images/treatment-help.jpg"
import uassistHelp from "../../../assets/images/uassist-help.jpg"
import ulabUniversity from "../../../assets/images/ulabUniversity.jpg"
import { useAppDispatch } from "../../../core/app/hooks"
import { fetchHelpDocuments } from "../../../core/app/slices/user/userApis"
import { UButton, UText } from "../../../ui-component"
import { Box, Divider, Grid, Link } from "../../../ui-component/mui.components"
import { convertBlobToPdfUrl } from "../../Patient/PatientList/util/commonUtil"

import HelpCard from "./HelpCard"

const HelpContent: FC = () => {
  const { t } = useTranslation("common")
  const dispatch = useAppDispatch()

  const cardItems = [
    {
      title: "help.cards.card1.title",
      subtitle: "help.cards.card1.subtitle",
      image: gettingStartedHelp,
      fileName: "getting_started.pdf", // LAB-0001582 - uDesign Cloud Help: Getting Started with uDesign Cloud
    },
    {
      title: "help.cards.card2.title",
      subtitle: "help.cards.card2.subtitle",
      image: accountHelp,
      fileName: "account_menu.pdf", // LAB-0001577 - uDesign Cloud Help: Account Menu
    },
    {
      title: "help.cards.card3.title",
      subtitle: "help.cards.card3.subtitle",
      image: patientListHelp,
      fileName: "patient_list.pdf", // LAB-0001578 - uDesign Cloud Help: Patient List
    },
    {
      title: "help.cards.card4.title",
      subtitle: "help.cards.card4.subtitle",
      image: uassistHelp,
      fileName: "uassist_workflow.pdf", // LAB-0001579 - uDesign Cloud Help: New Patient Workflow
    },
    {
      title: "help.cards.card5.title",
      subtitle: "help.cards.card5.subtitle",
      image: treatmentHelp,
      fileName: "treatment_plan_modification.pdf", // LAB-0001580 - uDesign Cloud Help: Treatment Planning Screen
    },
    {
      title: "help.cards.card6.title",
      subtitle: "help.cards.card6.subtitle",
      image: orderHelp,
      fileName: "ordering_usmile_aligners.pdf", // LAB-0001581 - uDesign Cloud Help: Ordering uSmile Aligners
    },
    {
      title: "help.cards.card7.title",
      subtitle: "help.cards.card7.subtitle",
      image: printHelp,
      fileName: "exporting_stl.pdf", // LAB-0001953 - uDesign® Cloud: Exporting STL Files For In-Office Printing
    },
    {
      title: "help.cards.card8.title",
      subtitle: "help.cards.card8.subtitle",
      image: scannerIntegrationHelp,
      fileName: "scanner_integration.pdf",
    },
    {
      title: "",
      subtitle: "",
      image: null,
      fileName: "",
    },
  ]

  const downloadHelpDoc = (fileName) => {
    dispatch(fetchHelpDocuments(fileName)).then((res: any) => {
      const link = document.createElement("a")
      link.href = convertBlobToPdfUrl(res.payload)
      // Open the downloaded PDF in a new tab
      window.open(link.href, "_blank")
    })
  }

  return (
    <Box
      data-testid={"helpContent"}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          paddingY: 2,
        }}
      >
        <UText variant={"h4"}>{t("help.title")}</UText>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "center",
          gap: 2,
          borderRadius: 2,
          padding: "32px 40px 48px 40px",
          boxShadow: 1,
          width: "79.6%",
          backgroundColor: "background.default",
          marginBottom: 6,
        }}
      >
        <UText
          variant={"h5"}
          sxProp={{
            py: 2,
          }}
        >
          {t("help.helpDocuments")}
        </UText>
        <Grid
          sx={{
            paddingBottom: "24px",
          }}
          justifyContent={"center"}
          container
          rowGap={2}
          columnGap={3}
        >
          {cardItems.map((item, index) => {
            return (
              <Grid
                item
                lg={3}
                md={4}
                sm={10}
                xs={12}
                sx={{ "&.MuiGrid-item": { maxWidth: "100%", flexGrow: 1 } }}
                key={"card-" + index}
                onClick={() => {
                  return item.fileName ? downloadHelpDoc(item.fileName) : null
                }}
              >
                {item.title ? (
                  <Link
                    sx={{
                      textDecoration: "none",
                    }}
                  >
                    <HelpCard
                      title={item.title}
                      subtitle={item.subtitle}
                      imageUrl={item.image}
                    />
                  </Link>
                ) : (
                  <span></span>
                )}
              </Grid>
            )
          })}
        </Grid>
        <Divider />
        <UText
          variant={"h5"}
          sxProp={{
            pt: "20px",
          }}
        >
          {t("help.uLabUniversity.title")}
        </UText>
        <Grid justifyContent={"center"} container rowGap={2} columnGap={3}>
          <Grid item lg={4} md={4}>
            <Box
              component={"img"}
              src={ulabUniversity}
              alt={"Ulab University"}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid item lg={7} md={7}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <UText
                sxProp={{
                  color: "#000",
                  paddingTop: 2,
                  paddingBottom: 3,
                }}
                variant={"body1"}
              >
                {t("help.uLabUniversity.content")}
              </UText>
              <UButton
                sxProp={{
                  fontSize: "15px",
                  letterSpacing: "0.46px",
                  lineHeight: "173.333%",
                }}
                onClickHandler={() => {
                  window.open(
                    "https://ulab.smiledesignuniversity.com/#/login",
                    "_blank",
                  )
                }}
                variant={"contained"}
                btnText={t("help.uLabUniversity.gotoBtn")}
                size={"large"}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default HelpContent
